'use client'
import { type FC, useMemo } from 'react'
import { Box, type StackProps, Text, VStack } from '@chakra-ui/react'
import { z } from 'zod'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import {
  FormProvider,
  GrandFormInputField,
  GrandFormSubmitButton,
  type Resolver,
  createPasswordSchema,
  useForm,
  zodResolver
} from '../grand-form'

export interface ChangePasswordSubmitData {
  oldPassword?: string
  password: string
}

type ChangePasswordFormValues = {
  oldPassword: string
  newPassword: string
  repeatPassword: string
}

export interface ChangePasswordFormOverrides {
  changePwdFormMsg?: string
  changePwdFormOldPwd?: string
  changePwdFormOldPwdRequired?: string
  changePwdFormNewPwd?: string
  changePwdFormNewPwdInvalid?: string
  changePwdFormRepeatPwd?: string
  changePwdFormRepeatPwdMismatch?: string
  changePwdFormBtnLabel?: string
  changePwdFormBtnLoading?: string
}

interface ChangePasswordFormProps extends Omit<StackProps, 'onSubmit'> {
  /** When true, includes a required "current password" field. */
  withOldPassword?: boolean
  onSubmit: (data: ChangePasswordSubmitData) => Promise<void> | void
  error?: string
}

const ChangePasswordForm: FC<ChangePasswordFormProps> = ({
  withOldPassword,
  onSubmit,
  error,
  ...stackProps
}) => {
  const overrides = useOverridesContext<keyof ChangePasswordFormOverrides>()
  const description = overrideText(
    'Minst 6 tecken, varav en stor bokstav, en liten bokstav och en siffra.',
    overrides?.changePwdFormMsg
  )
  const oldLabel = overrideText('Gammalt lösenord', overrides?.changePwdFormOldPwd)
  const oldRequiredMsg = overrideText(
    'Gammalt lösenord krävs.',
    overrides?.changePwdFormOldPwdRequired
  )
  const newLabel = overrideText('Nytt lösenord', overrides?.changePwdFormNewPwd)
  const newInvalidMsg = overrideText(
    'Lösenordet måste vara minst 6 tecken och innehålla stor och liten bokstav samt en siffra.',
    overrides?.changePwdFormNewPwdInvalid
  )
  const repeatLabel = overrideText('Bekräfta lösenord', overrides?.changePwdFormRepeatPwd)
  const mismatchMsg = overrideText(
    'Lösenorden matchar inte.',
    overrides?.changePwdFormRepeatPwdMismatch
  )
  const btnLabel = overrideText('Spara', overrides?.changePwdFormBtnLabel)
  const btnLoading = overrideText('Sparar…', overrides?.changePwdFormBtnLoading)

  const schema = useMemo(
    () =>
      z
        .object({
          oldPassword: withOldPassword
            ? z.string().min(1, oldRequiredMsg)
            : z.string().optional().default(''),
          newPassword: createPasswordSchema({
            requiredMessage: newInvalidMsg,
            invalidMessage: newInvalidMsg
          }),
          repeatPassword: z.string().min(1, mismatchMsg)
        })
        .refine((data) => data.newPassword === data.repeatPassword, {
          message: mismatchMsg,
          path: ['repeatPassword']
        }),
    [withOldPassword, oldRequiredMsg, newInvalidMsg, mismatchMsg]
  )

  const methods = useForm<ChangePasswordFormValues>({
    defaultValues: { oldPassword: '', newPassword: '', repeatPassword: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema) as Resolver<ChangePasswordFormValues>
  })

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    await onSubmit({
      password: values.newPassword,
      ...(withOldPassword ? { oldPassword: values.oldPassword } : {})
    })
    methods.reset({ oldPassword: '', newPassword: '', repeatPassword: '' })
  }

  return (
    <FormProvider {...methods}>
      <Box as="form" onSubmit={methods.handleSubmit(handleSubmit)} w="full">
        <VStack align="stretch" spacing={4} w="full" {...stackProps}>
          {withOldPassword ? (
            <GrandFormInputField
              control={methods.control}
              label={oldLabel}
              name="oldPassword"
              placeholder={oldLabel}
              required
              type="password"
            />
          ) : null}
          <GrandFormInputField
            control={methods.control}
            description={description}
            label={newLabel}
            name="newPassword"
            placeholder={newLabel}
            required
            type="password"
          />
          <GrandFormInputField
            control={methods.control}
            label={repeatLabel}
            name="repeatPassword"
            placeholder={repeatLabel}
            required
            type="password"
          />
          {error ? (
            <Text color="status.error" fontSize="sm">
              {error}
            </Text>
          ) : null}
          <GrandFormSubmitButton disableIfInvalid={false} loadingText={btnLoading}>
            {btnLabel}
          </GrandFormSubmitButton>
        </VStack>
      </Box>
    </FormProvider>
  )
}

export default ChangePasswordForm
